import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/components/ui/use-toast'
import { Loader2, CheckCircle, GitBranch, Shield, Zap } from 'lucide-react'

export default function LandingPage() {
  const { toast } = useToast()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    company: '',
    githubUsername: '',
    useCase: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [showBetaRequired, setShowBetaRequired] = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/overview', { replace: true })
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    // Check if user was redirected because they need beta access
    if (searchParams.get('betaRequired') === 'true') {
      setShowBetaRequired(true)
      // Scroll to the beta signup form
      setTimeout(() => {
        document.getElementById('beta-signup')?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch('/api/v1/beta-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          useCase: formData.useCase || undefined
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setIsSubmitted(true)
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to submit signup. Please try again.',
          variant: 'destructive'
        })
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to submit signup. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="container mx-auto px-4 pt-20 pb-16">
        <div className="text-center max-w-3xl mx-auto">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/15 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
            <GitBranch className="h-4 w-4" />
            Beta access available now
          </div>
          <h1 className="text-5xl font-bold tracking-tight mb-6">
            Network Configuration Analysis for Modern Infrastructure
          </h1>
          <p className="text-xl text-muted-foreground mb-8">
            Validate your network configurations before deployment. Catch misconfigurations, 
            ensure compliance, and prevent outages with automated analysis.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <Card>
            <CardHeader>
              <Shield className="h-10 w-10 text-primary mb-4" />
              <CardTitle>Proactive Validation</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Catch configuration errors before they impact production. 
                Validate routing, ACLs, and compliance policies automatically.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <GitBranch className="h-10 w-10 text-primary mb-4" />
              <CardTitle>Git Integration</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Seamlessly integrates with your existing Git workflow. 
                Analyze configurations on every pull request.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Zap className="h-10 w-10 text-primary mb-4" />
              <CardTitle>Fast Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Get results in seconds, not hours. Built for speed and 
                scalability with modern infrastructure in mind.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Beta Signup Form */}
      <section className="container mx-auto px-4 py-16" id="beta-signup">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl">Request Beta Access</CardTitle>
            <CardDescription>
              {showBetaRequired ? (
                <div className="text-orange-600 font-medium">
                  Beta access is required to use EtherPilot. Please submit your request below.
                </div>
              ) : (
                <div>
                  <p>Be among the first to experience automated network configuration analysis.</p>
                  <p className="text-sm mt-2">Sign in with Google, Microsoft, or GitHub once approved.</p>
                </div>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Thank you for your interest!</h3>
                <p className="text-muted-foreground">
                  We'll review your application and get back to you soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="text-sm font-medium mb-1 block">
                      Name *
                    </label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="githubUsername" className="text-sm font-medium mb-1 block">
                      GitHub Username (optional)
                    </label>
                    <Input
                      id="githubUsername"
                      value={formData.githubUsername}
                      onChange={(e) => setFormData({ ...formData, githubUsername: e.target.value })}
                      placeholder="johndoe"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="text-sm font-medium mb-1 block">
                      Email *
                    </label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="text-sm font-medium mb-1 block">
                      Company (optional)
                    </label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Acme Corp"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="useCase" className="text-sm font-medium mb-1 block">
                    Tell us about your use case (optional)
                  </label>
                  <Textarea
                    id="useCase"
                    value={formData.useCase}
                    onChange={(e) => setFormData({ ...formData, useCase: e.target.value })}
                    placeholder="Describe how you plan to use EtherPilot..."
                    rows={4}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Request Beta Access'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t mt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-sm text-muted-foreground">
            © 2026 EtherPilot. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}
